// src/admin/pages/banners/bannerService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { Banner } from './banner';

const BANNERS_COLLECTION = 'banners';
const STORAGE_PATH = 'banners';

export class BannerService {
  // Upload banner image
  static async uploadBannerImage(bannerId: string, file: File): Promise<string> {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `${bannerId}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, `${STORAGE_PATH}/${fileName}`);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      return downloadURL;
    } catch (error) {
      console.error('Error uploading banner image:', error);
      throw error;
    }
  }

  // Delete image from storage by URL
  static async deleteBannerImageFromStorage(imageUrl: string): Promise<void> {
    try {
      const urlParts = imageUrl.split('/o/');
      if (urlParts.length < 2) return;

      const pathWithParams = urlParts[1];
      const filePath = decodeURIComponent(pathWithParams.split('?')[0]);
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting banner image from storage:', error);
      // Non-fatal — continue even if storage delete fails
    }
  }

  /**
   * Get all banners (there should only ever be 0 or 1).
   */
  static async getBanners(): Promise<Banner[]> {
    try {
      const snapshot = await getDocs(collection(db, BANNERS_COLLECTION));
      return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Banner;
      });
    } catch (error) {
      console.error('Error fetching banners:', error);
      throw error;
    }
  }

  /**
   * Create a new banner (image-only).
   * Returns the new document ID.
   */
  static async createBanner(
    userId: string,
    imageFile: File
  ): Promise<string> {
    try {
      // Create the doc first to get an ID
      const docRef = await addDoc(collection(db, BANNERS_COLLECTION), {
        imageUrl: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
      });

      // Upload image using the doc ID
      const imageUrl = await this.uploadBannerImage(docRef.id, imageFile);

      // Write the URL back
      await updateDoc(docRef, { imageUrl });

      return docRef.id;
    } catch (error) {
      console.error('Error creating banner:', error);
      throw error;
    }
  }

  /**
   * Update an existing banner's image.
   * Deletes the old image from storage before uploading the new one.
   */
  static async updateBanner(
    bannerId: string,
    imageFile: File
  ): Promise<void> {
    try {
      const bannerRef = doc(db, BANNERS_COLLECTION, bannerId);
      const bannerDoc = await getDoc(bannerRef);

      if (!bannerDoc.exists()) {
        throw new Error('Banner not found');
      }

      const currentData = bannerDoc.data();

      // Delete old image
      if (currentData.imageUrl) {
        await this.deleteBannerImageFromStorage(currentData.imageUrl);
      }

      // Upload new image
      const imageUrl = await this.uploadBannerImage(bannerId, imageFile);

      await updateDoc(bannerRef, {
        imageUrl,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating banner:', error);
      throw error;
    }
  }

  /**
   * Delete a banner document and its associated image from storage.
   */
  static async deleteBanner(bannerId: string): Promise<void> {
    try {
      const bannerRef = doc(db, BANNERS_COLLECTION, bannerId);
      const bannerDoc = await getDoc(bannerRef);

      if (bannerDoc.exists()) {
        const data = bannerDoc.data();
        if (data.imageUrl) {
          await this.deleteBannerImageFromStorage(data.imageUrl);
        }
      }

      await deleteDoc(bannerRef);
    } catch (error) {
      console.error('Error deleting banner:', error);
      throw error;
    }
  }
}